import React, { useEffect, useState, useRef } from "react";
import { IconButton, Typography } from "@mhome/ui";
import { Air } from "@mui/icons-material";
import { SyncBadge } from "../../common/badges";
import FanSpeedDialog from "../twin-widget-dialog/fan-speed-dialog-1";
import FixedSelectInput from "../../input/fixed-select-input";

const Fan1Widget = ({ widget, onValueUpdate, pattern }) => {
  const [fanMode, setFanMode] = useState("");
  const [fanSpeed, setFanSpeed] = useState(0);
  const [modeOptions, setModeOptions] = useState([]);
  const [speedMinMax, setSpeedMinMax] = useState({ min: 0, max: 100 });
  const [speedDialogOpen, setSpeedDialogOpen] = useState(false);
  const [widgetData, setWidgetData] = useState(null);
  const newSpeedRef = useRef(0);

  useEffect(() => {
    if (!widget) return;
    setWidgetData(widget);

    // Handle fan mode from $w1
    if (widget.items?.$w1) {
      const currentMode = widget.items.$w1.value || "";
      setFanMode(currentMode);

      // Set mode options from enums or provide defaults
      const options = widget.items.$w1.options || [];
      setModeOptions(options);
    }

    // Handle fan speed from $w2 (if present)
    if (widget.items?.$w2) {
      const currentSpeed = Number(widget.items.$w2.value) || 0;
      setFanSpeed(currentSpeed);
      setSpeedMinMax({
        min: widget.items.$w2.min ?? 0,
        max: widget.items.$w2.max ?? 100,
      });
    }
  }, [widget]);

  const handleModeChange = (newMode) => {
    if (pattern && pattern.endsWith("_R")) return;

    setFanMode(newMode);

    if (widgetData?.items?.$w1) {
      const updatedWidget = {
        ...widgetData.items.$w1,
        value: newMode,
      };
      onValueUpdate(updatedWidget);
    }
  };

  const updateVisualSpeed = (newValue) => {
    const adjustedValue = Math.min(
      Math.max(newValue, speedMinMax.min),
      speedMinMax.max
    );
    setFanSpeed(adjustedValue);
    newSpeedRef.current = adjustedValue;
  };

  const handleSpeedChange = (newValue) => {
    if (!widgetData?.items?.$w2) return; // Don't handle speed changes if speed widget doesn't exist
    updateVisualSpeed(newValue);
  };

  const handleSpeedUpdate = (newSpeed) => {
    if (!widgetData?.items?.$w2) return; // Don't handle speed updates if speed widget doesn't exist

    if (widgetData?.items?.$w2) {
      const updatedWidget = {
        ...widgetData.items.$w2,
        value: newSpeed,
      };
      onValueUpdate(updatedWidget);
    }
  };

  const handleFanIconClick = (e) => {
    if (disabled || !hasSpeedControl) return;
    e.preventDefault();
    e.stopPropagation();
    setSpeedDialogOpen(true);
  };

  const getFanIcon = () => {
    const isActive = fanMode && fanMode !== "off";
    return (
      <Air
        style={{
          fontSize: "1.2rem",
          color: isActive
            ? "hsl(var(--primary))"
            : "hsl(var(--muted-foreground))",
        }}
        className={isActive ? "animate-spin" : ""}
      />
    );
  };

  const disabled = pattern && pattern.endsWith("_R");
  const hasSpeedControl = !!widgetData?.items?.$w2;

  if (!widgetData) {
    return (
      <div className="h-full flex items-center justify-center">
        <span className="text-xs">Loading...</span>
      </div>
    );
  }

  return (
    <div className="relative flex items-center justify-between h-full w-full gap-1 px-1 rounded-2xl overflow-hidden">
      {pattern && pattern.endsWith("_W") && <SyncBadge />}

      {/* Fan Icon - Clickable if has speed control */}
      {hasSpeedControl ? (
        <IconButton
          onClick={handleFanIconClick}
          disabled={disabled}
          className="px-1.5 py-1 rounded-2xl flex items-center h-full gap-0.5 min-w-auto"
          style={{
            backgroundColor: disabled ? "hsl(var(--card))" : "transparent",
            border: disabled ? "none" : "1px solid",
            borderColor: disabled ? "transparent" : "hsl(var(--border))",
          }}
        >
          {getFanIcon()}
          <span
            className="text-xs font-medium leading-none"
            style={{
              color:
                fanMode && fanMode !== "off"
                  ? "hsl(var(--primary))"
                  : "hsl(var(--muted-foreground))",
            }}
          >
            {fanSpeed}
          </span>
        </IconButton>
      ) : (
        <div className="flex items-center px-1">{getFanIcon()}</div>
      )}

      {/* Fan Mode Selection */}
      <div className="flex-1 ml-2 h-7">
        <FixedSelectInput
          valueSpec={{
            value: fanMode,
            enums: modeOptions,
            readOnly: disabled,
          }}
          onChange={handleModeChange}
          onError={() => {}}
        />
      </div>

      {/* Fan Speed Dialog */}
      {hasSpeedControl && (
        <FanSpeedDialog
          open={speedDialogOpen}
          onClose={() => setSpeedDialogOpen(false)}
          speed={fanSpeed}
          onChange={handleSpeedChange}
          onSpeedUpdate={handleSpeedUpdate}
          min={speedMinMax.min}
          max={speedMinMax.max}
          disabled={disabled}
        />
      )}
    </div>
  );
};

export default Fan1Widget;
